/**
 * azure-functions/shared/tasks/closure.cjs
 *
 * One rule for closing an ad-hoc task (state.adhocTasks, Tier 3 of the one
 * task system), shared by the browser and state-write — founder decision A on
 * GH #7704: EVERY close carries a closure record, and the server enforces it.
 * TRYGGR_INTENT.md §12: an action without evidence is not complete; a status
 * that says "closed" with nothing recorded is a governance gap wearing the
 * clothes of a finished record.
 *
 * A closure record is one entry appended to `task.closures`:
 *   person  { closedAt, closedBy, closedByName, comment, evidenceIds[] }
 *           — what the person did: their own words (CloseTaskPanel, the
 *             leaver hand-over note), or for an access change the change
 *             they approved, which is itself the evidence. closedBy is
 *             stamped by state-write from the signed-in user, never taken
 *             from the payload.
 *   system  { closedAt, closedBy: 'system', system: true, reason, trigger, ranBy? }
 *           — an automatic close (a sweep) names why it closed and the event
 *             that triggered it. It never pretends a person did the work.
 *             A sweep that runs in a browser is stamped ranBy = that session's
 *             user by state-write: it cannot be proven to be the system.
 *
 * The browser re-exports this file (src/utils/taskClosure.js), so the screen
 * and the server cannot disagree on what a valid close is.
 */

'use strict';

// Status transitions (formerly src/utils/taskTransitions.js, now re-exported
// from here): closed reopens to open only, never straight to in-progress.
const TASK_TRANSITIONS = Object.freeze({
  open: Object.freeze(['in-progress', 'closed']),
  'in-progress': Object.freeze(['open', 'closed']),
  closed: Object.freeze(['open']),
});
const TASK_STATUSES = Object.freeze(Object.keys(TASK_TRANSITIONS));

function isValidTaskTransition(from, to) {
  if (from === to) return true;
  const allowed = TASK_TRANSITIONS[from];
  return allowed ? allowed.includes(to) : false;
}

function getAllowedNextStatuses(currentStatus) {
  return TASK_TRANSITIONS[currentStatus] ? [...TASK_TRANSITIONS[currentStatus]] : [];
}

const hasText = (v) => typeof v === 'string' && v.trim().length > 0;

/** A person's closure record. `comment` is required — their own words. */
function personClosure({ by, byName = null, comment, evidenceIds = [], at = new Date().toISOString() }) {
  if (!hasText(comment)) throw new Error('A closure needs the closer\'s own comment.');
  return {
    closedAt: at,
    closedBy: by || null,
    closedByName: byName || null,
    comment: comment.trim(),
    evidenceIds: Array.isArray(evidenceIds) ? evidenceIds : [],
  };
}

/** An automatic close's record: why, and which event triggered it. */
function systemClosure({ reason, trigger, at = new Date().toISOString() }) {
  if (!hasText(reason) || !hasText(trigger)) throw new Error('A system closure names its reason and its trigger.');
  return { closedAt: at, closedBy: 'system', system: true, reason: reason.trim(), trigger: trigger.trim() };
}

/** Is `entry` a closure record the guard accepts? */
function isValidClosureEntry(entry) {
  if (!entry || typeof entry !== 'object' || !hasText(entry.closedAt)) return false;
  if (entry.system === true) return entry.closedBy === 'system' && hasText(entry.reason) && hasText(entry.trigger);
  return hasText(entry.comment);
}

/**
 * Close `task` with `closure`: the status, the summary stamps every reader
 * already uses (closedAt, closedBy, closedReason / closureComment), and the
 * record appended to `closures`. The only way the client closes a task.
 */
function closeTask(task, closure) {
  if (!isValidClosureEntry(closure)) throw new Error('closeTask() needs a valid closure record.');
  return {
    ...task,
    status: 'closed',
    closedAt: closure.closedAt,
    closedBy: closure.closedBy,
    ...(closure.system ? { closedReason: closure.reason } : { closureComment: closure.comment }),
    closures: [...(Array.isArray(task.closures) ? task.closures : []), closure],
  };
}

/**
 * A server-side closer's task, with its system closure record appended. The
 * closers that write the blob directly (BC/DR retest, leader dialogue, the
 * measurement sweeps) build their own closed task — this adds the record
 * every close carries without changing any field they already write.
 */
function withSystemClosure(closedTask, { trigger, at }) {
  const when = at instanceof Date ? at.toISOString() : at || closedTask.closedAt || new Date().toISOString();
  const entry = systemClosure({ reason: String(closedTask.closedReason || 'closed by the system'), trigger, at: when });
  return { ...closedTask, closures: [...(Array.isArray(closedTask.closures) ? closedTask.closures : []), entry] };
}

/**
 * state-write's guard for state.adhocTasks. Restore, don't reject: only the
 * offending task changes, the rest of the save lands, and every restore is
 * returned for state-write to report.
 *
 *   - newly closed (stored not closed, or new) without a NEW valid closure
 *     record (closures grew compared with the stored copy — a reopened task
 *     keeps its old closures, so "has a record" alone would pass a stale
 *     re-close) → the stored task comes back; a new task is kept open.
 *   - a transition outside TASK_TRANSITIONS (closed → in-progress, any
 *     unknown status such as 'overdue') → the stored task comes back; a new
 *     task with an unknown status is kept open.
 *   - a stored task's closures are append-only: a payload that drops or
 *     rewrites earlier entries gets the stored ones back in front.
 *
 * @returns {{ tasks: object[], restored: { taskId: string, rule: string }[] }}
 */
function guardAdhocTaskClosures(incoming, stored, { actor = null, now = null } = {}) {
  if (!Array.isArray(incoming)) return { tasks: incoming, restored: [] };
  const priorById = new Map((Array.isArray(stored) ? stored : []).filter((t) => t && t.id).map((t) => [t.id, t]));
  const restored = [];
  const tasks = incoming.map((t) => {
    if (!t || typeof t !== 'object' || !t.id) return t;
    const prior = priorById.get(t.id) || null;
    const priorStatus = prior ? prior.status || 'open' : null;
    const status = t.status || 'open';
    const priorClosures = prior && Array.isArray(prior.closures) ? prior.closures : [];
    const sentClosures = Array.isArray(t.closures) ? t.closures : [];

    // Append-only closures on a stored task.
    let task = t;
    const prefixKept = priorClosures.every((c, i) => JSON.stringify(sentClosures[i]) === JSON.stringify(c));
    if (prior && !prefixKept) {
      task = { ...t, closures: [...priorClosures, ...sentClosures.filter((c) => !priorClosures.some((p) => JSON.stringify(p) === JSON.stringify(c)))] };
      restored.push({ taskId: t.id, rule: 'closures-append-only' });
    }

    // A closure record is written only by the save that closes the task. An
    // entry appended on any other save (to an open task, or to one already
    // closed) would put words in the audit trail no close stands behind — so
    // it is dropped, whatever it claims (co-owner round 2, GH #7704).
    const newlyClosed = status === 'closed' && priorStatus !== 'closed';
    const allClosures = Array.isArray(task.closures) ? task.closures : [];
    if (!newlyClosed && allClosures.length > priorClosures.length) {
      task = { ...task, closures: allClosures.slice(0, priorClosures.length) };
      restored.push({ taskId: t.id, rule: 'closure-entry-without-close' });
    }

    // A document-scan review task (shared/controlDocScan/task.js): its link
    // to the scan and its undecided-suggestion count are server-owned, and it
    // cannot close while a suggestion is still undecided — an action without
    // its evidence is not complete (TRYGGR_INTENT.md §12).
    if (prior && prior.sourceType === 'control-doc-scan') {
      task = {
        ...task,
        sourceType: prior.sourceType,
        linkedScanId: prior.linkedScanId,
        scanPendingCount: prior.scanPendingCount,
      };
      if (newlyClosed && Number(prior.scanPendingCount) > 0) {
        restored.push({ taskId: t.id, rule: 'scan-suggestions-undecided' });
        return prior;
      }
    }

    // A status unchanged from the stored copy is never flagged — a legacy
    // value (the retired engine template's 'overdue') must not report a
    // revert on every save, nor freeze the task's other fields.
    if (prior && status === priorStatus) return task;
    if (!TASK_STATUSES.includes(status)) {
      restored.push({ taskId: t.id, rule: 'unknown-status' });
      return prior || { ...task, status: 'open' };
    }
    // A stored status outside the table is read as 'open', so a legacy task
    // can still be worked and closed with a record.
    const fromStatus = TASK_STATUSES.includes(priorStatus) ? priorStatus : 'open';
    if (prior && !isValidTaskTransition(fromStatus, status)) {
      restored.push({ taskId: t.id, rule: `transition-${priorStatus}-to-${status}` });
      return prior;
    }
    if (newlyClosed) {
      const fresh = (Array.isArray(task.closures) ? task.closures : []).slice(priorClosures.length);
      if (!fresh.length || !fresh.every(isValidClosureEntry)) {
        restored.push({ taskId: t.id, rule: 'close-needs-closure-record' });
        if (prior) return prior;
        const { closedAt: _a, closedBy: _b, closedReason: _c, closureComment: _d, ...rest } = task;
        return { ...rest, status: 'open' };
      }
      // Who closed it is the signed-in user, never the payload's claim.
      if (actor) {
        const kept = (Array.isArray(task.closures) ? task.closures : []).slice(0, priorClosures.length);
        const stamped = fresh.map((c) =>
          c.system ? { ...c, ranBy: actor } : { ...c, closedBy: actor, ...(now ? { closedAt: now } : {}) }
        );
        const last = stamped[stamped.length - 1];
        return {
          ...task,
          closures: [...kept, ...stamped],
          closedBy: last.system ? 'system' : actor,
          ...(now && !last.system ? { closedAt: now } : {}),
        };
      }
    }
    return task;
  });
  return { tasks, restored };
}

/** Fields a close writes on the task (closeTask), and nothing else. */
const CLOSE_FIELDS = new Set(['status', 'closedAt', 'closedBy', 'closedReason', 'closureComment', 'closures']);

/**
 * The patch path's answer to "is this close already in place?" (FDNA-01D).
 *
 * A close is not a plain field value: the guard above stamps it server-side
 * (`ranBy` on a system record; the actor and the server clock on a person's),
 * so the stored task never equals what the closing save sent. A second
 * delivery of that same close — a save whose request was aborted after it
 * landed and is replayed from the teardown backup, or a second tab whose
 * sweep observed the same resolved condition — then reads as someone else
 * having changed the task, and a person is asked to adjudicate a conflict the
 * system had with itself.
 *
 * A close is already in place when the stored task is closed by the same
 * close: a system record from the same trigger for the same reason, or a
 * person's record by this caller with the same comment. Its close operations
 * are dropped; every other operation in the save is applied as usual. A
 * different close (another person, another comment, another trigger) is
 * left alone and is still named as a clash.
 *
 * @param {object[]} storedTasks  state.adhocTasks as stored now
 * @param {object[]} ops          the save's patch operations (statePatch.cjs)
 * @param {string|null} actor     the signed-in caller
 * @returns {{ ops: object[], settled: string[] }}
 */
function settleRepeatedTaskCloses(storedTasks, ops, actor) {
  if (!Array.isArray(storedTasks) || !Array.isArray(ops)) return { ops, settled: [] };
  const taskOf = (op) =>
    op &&
    op.op === 'set' &&
    Array.isArray(op.path) &&
    op.path.length === 3 &&
    op.path[0] === 'adhocTasks' &&
    op.path[1] &&
    op.path[1].k === 'id' &&
    CLOSE_FIELDS.has(op.path[2])
      ? op.path[1].v
      : null;

  const settled = new Set();
  for (const op of ops) {
    const id = taskOf(op);
    if (id == null || op.path[2] !== 'status' || op.after !== 'closed' || op.before === 'closed') continue;
    const closuresOp = ops.find((o) => taskOf(o) === id && o.path[2] === 'closures');
    const sent = closuresOp && Array.isArray(closuresOp.after) ? closuresOp.after : [];
    const before = closuresOp && Array.isArray(closuresOp.before) ? closuresOp.before : [];
    if (sent.length !== before.length + 1) continue;
    const mine = sent[sent.length - 1];
    const stored = storedTasks.find((t) => t && t.id === id);
    if (!stored || stored.status !== 'closed' || !Array.isArray(stored.closures)) continue;
    const theirs = stored.closures[stored.closures.length - 1];
    if (!isValidClosureEntry(mine) || !isValidClosureEntry(theirs)) continue;
    const same = mine.system
      ? theirs.system === true && theirs.trigger === mine.trigger && theirs.reason === mine.reason
      : theirs.system !== true && !!actor && theirs.closedBy === actor && theirs.comment === mine.comment;
    if (same) settled.add(id);
  }
  if (!settled.size) return { ops, settled: [] };
  return { ops: ops.filter((op) => !settled.has(taskOf(op))), settled: [...settled] };
}

module.exports = {
  TASK_TRANSITIONS,
  TASK_STATUSES,
  isValidTaskTransition,
  getAllowedNextStatuses,
  personClosure,
  systemClosure,
  isValidClosureEntry,
  closeTask,
  withSystemClosure,
  guardAdhocTaskClosures,
  settleRepeatedTaskCloses,
};
